'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { PageForm, type PageFormRef } from '@/components/partials/pages'
import { useCreatePage } from '@/hooks'
import { type CreatePageParams } from '@/types/page'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const pageFormRef = useRef<PageFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createPage, isPending: isLoading } = useCreatePage()

  const handleSubmit = (data: CreatePageParams) => {
    createPage(data, {
      onSuccess: async newPage => {
        setIsRedirecting(true)
        queryClient.setQueryData(['page', newPage.id], newPage)
        await queryClient.invalidateQueries({ queryKey: ['pages'] })
        toast.success(t('created_successfully'))
        router.push(`/pages/${newPage.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreatePageParams) => {
    createPage(data, {
      onSuccess: async newPage => {
        queryClient.setQueryData(['page', newPage.id], newPage)
        await queryClient.invalidateQueries({ queryKey: ['pages'] })
        const detailUrl = `/pages/${newPage.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        pageFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (pageFormRef.current) {
        pageFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/pages')
  }

  return (
    <PageForm
      key={formKey}
      ref={pageFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
